import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { THEMES, getThemeById } from '@styles/colorThemes';

export const ThemeContext = createContext();

const THEME_STORAGE_KEY = 'portfolio.theme';
const THEME_SOURCE_STORAGE_KEY = 'portfolio.theme.source';
const THEME_SOURCE_USER = 'user';
const THEME_SOURCE_SYSTEM = 'system';
const THEME_TRANSITION_CLASS = 'theme-switching';

const DEFAULT_THEME_ID = 'carbon';

const getInitialThemeState = () => {
  if (typeof window === 'undefined') {
    return {
      themeId: DEFAULT_THEME_ID,
      themeSource: THEME_SOURCE_USER,
    };
  }

  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored && THEMES.some(theme => theme.id === stored)) {
    return {
      themeId: stored,
      themeSource: THEME_SOURCE_USER,
    };
  }

  return {
    themeId: DEFAULT_THEME_ID,
    themeSource: THEME_SOURCE_USER,
  };
};

const applyThemeToDocument = (themeId, withTransition = false) => {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  if (withTransition) {
    root.classList.add(THEME_TRANSITION_CLASS);
  }

  root.setAttribute('data-theme', themeId);

  const themeDefinition = getThemeById(themeId);
  const colorScheme = themeDefinition.isDark ? 'dark' : 'light';
  root.style.colorScheme = colorScheme;

  if (withTransition) {
    window.setTimeout(() => {
      root.classList.remove(THEME_TRANSITION_CLASS);
    }, 260);
  }
};

export const ThemeProvider = ({ children }) => {
  const [themeState, setThemeState] = useState(getInitialThemeState);
  const { themeId, themeSource } = themeState;
  const hasHydratedRef = useRef(false);

  const setThemeId = nextThemeId => {
    if (!THEMES.some(theme => theme.id === nextThemeId)) {
      return;
    }

    setThemeState({
      themeId: nextThemeId,
      themeSource: THEME_SOURCE_USER,
    });
  };

  useEffect(() => {
    applyThemeToDocument(themeId, hasHydratedRef.current);
    hasHydratedRef.current = true;

    if (typeof window !== 'undefined') {
      if (themeSource === THEME_SOURCE_USER) {
        window.localStorage.setItem(THEME_STORAGE_KEY, themeId);
        window.localStorage.setItem(THEME_SOURCE_STORAGE_KEY, THEME_SOURCE_USER);
      } else {
        window.localStorage.removeItem(THEME_STORAGE_KEY);
        window.localStorage.setItem(THEME_SOURCE_STORAGE_KEY, THEME_SOURCE_SYSTEM);
      }
    }
  }, [themeId, themeSource]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const matchMedia = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = event => {
      setThemeState(previous => {
        if (previous.themeSource !== THEME_SOURCE_SYSTEM) {
          return previous;
        }

        const nextId = event.matches ? 'midnight' : 'default';
        return {
          themeId: nextId,
          themeSource: THEME_SOURCE_SYSTEM,
        };
      });
    };

    matchMedia.addEventListener('change', handleChange);
    return () => matchMedia.removeEventListener('change', handleChange);
  }, []);

  const value = useMemo(
    () => ({
      themeId,
      themes: THEMES,
      setThemeId,
    }),
    [themeId],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
